import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex, Box } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, EmotionalUpliftEntry } from '../../services/analyticsService';

interface EmotionalUpliftChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const EmotionalUpliftChart: React.FC<EmotionalUpliftChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<EmotionalUpliftEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getEmotionalUplift(businessIdentifier);
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch emotional uplift data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    // Sort by delta descending
    const sorted = [...data].sort((a, b) => b.delta - a.delta);
    const conversationIds = sorted.map((d) => d.conversation_id.slice(0, 8));
    const deltas = sorted.map((d) => d.delta);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = sorted[idx];
          if (!item) return '';
          const sign = item.delta >= 0 ? '+' : '';
          return `<b>${item.conversation_id.slice(0, 8)}</b><br/>Start: ${item.start_score}<br/>End: ${item.end_score}<br/>Uplift: ${sign}${item.delta}`;
        },
      },
      grid: {
        left: '3%',
        right: '10%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Sentiment Change (Start \u2192 End)',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: conversationIds,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: deltas.map((delta) => ({
            value: delta,
            itemStyle: {
              color: delta >= 0 ? '#48BB78' : '#F56565',
              borderRadius: delta >= 0 ? [0, 4, 4, 0] : [4, 0, 0, 4],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: (params: any) => {
              const sign = params.value >= 0 ? '+' : '';
              return `${sign}${params.value}`;
            },
          },
        },
      ],
    };
  };

  const hasData = data && data.length > 0;

  return (
    <ChartCard title="Emotional Uplift (Start vs End)" isLoading={isLoading} error={error}>
      {hasData ? (
        <Box maxH="350px" overflowY="auto">
          <ReactECharts
            option={getChartOption()}
            style={{ height: Math.max(300, data!.length * 32) + 'px', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </Box>
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default EmotionalUpliftChart;
